import { NavLink } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  CandlestickChart,
  CreditCard,
  Layers,
  LayoutDashboard,
  LineChart,
  Palette,
  SlidersHorizontal,
  Star,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cx } from '@/lib/format';
import { useAuth } from '@/lib/auth';
import { DATA_MODE } from '@/lib/queries';
import { getMarketStatus } from '@/lib/marketStatus';
import styles from './appShell.module.scss';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: 'Desk',
    items: [
      { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/markets', label: 'Markets', icon: LineChart },
      { to: '/screener', label: 'Screener', icon: SlidersHorizontal },
    ],
  },
  {
    label: 'Universe',
    items: [
      { to: '/stocks', label: 'Stocks', icon: CandlestickChart },
      { to: '/etfs', label: 'ETFs', icon: Layers },
      { to: '/watchlist', label: 'Watchlist', icon: Star },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { to: '/portfolio', label: 'Portfolio', icon: Briefcase },
      { to: '/alerts', label: 'Alerts', icon: Bell },
      { to: '/pricing', label: 'Plans', icon: CreditCard },
    ],
  },
  {
    label: 'System',
    items: [{ to: '/design', label: 'Design lab', icon: Palette }],
  },
];

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const status = getMarketStatus();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const groups = NAV.filter((group) => group.label !== 'System' || isAdmin);

  return (
    <>
      <div className={cx(styles.overlay, open && styles.overlayVisible)} onClick={onClose} />
      <aside className={cx(styles.sidebar, open && styles.sidebarOpen)}>
        <div className={styles.brandRow}>
          <NavLink to="/" className={styles.brand} onClick={onClose}>
            <img src="/mark.svg" alt="" className={styles.brandMark} />
            <span className={styles.brandText}>
              <span className={styles.brandName}>StockWatcher</span>
              <span className={styles.brandPro}>PRO</span>
            </span>
          </NavLink>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>

        <div className={styles.navScroll}>
          {groups.map((group) => (
            <nav key={group.label} className={styles.navGroup} aria-label={group.label}>
              <span className={styles.navLabel}>{group.label}</span>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => cx(styles.navItem, isActive && styles.navItemActive)}
                    onClick={onClose}
                  >
                    <Icon size={15} strokeWidth={1.75} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          ))}
        </div>

        <div className={styles.sidebarFoot}>
          <div className={styles.marketPill}>
            <span className={cx(styles.statusDot, styles[`status-${status.state}`])} />
            <span className={styles.marketPillLabel}>US markets</span>
            <span className={styles.marketPillValue}>{status.label}</span>
          </div>
          <div className={styles.demoNote}>
            <span className={styles.demoTag}>{DATA_MODE === 'live' ? 'Live' : 'Preview'}</span>
            <span>
              {DATA_MODE === 'live'
                ? '15-min delayed · SEC fundamentals'
                : 'Demo fixtures · live feed pending'}
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}
